import { ROUTE_PATHS } from '@modules/core/config/modules';
import { AuthService } from '@public/auth/services';
import { isApiError } from '@shared/utils/api/client';
import { APUtilEventBus as Emitter } from '@shared/utils/event-bus/event-bus.util';

import type { IAuthClientConfig, IAuthError, IAuthState } from './types';
import type { IAuthSessionResponse } from '@public/auth/services';
import type { ApiError } from '@shared/utils/api/client';

import { AuthInitializationError } from './errors';

/**
 * Storage key for the CSRF state token stored before the AS redirect.
 * The code_verifier is generated server-side and never exposed to the frontend.
 */
const AUTH_STATE_KEY = 'auth_state';

function categorizeError(error: unknown): IAuthError {
  const timestamp = Date.now();

  const isErrorLike = (
    e: unknown
  ): e is { message?: string; code?: string; errorCode?: string } => {
    return typeof e === 'object' && e !== null;
  };

  if (!isErrorLike(error)) {
    return { type: 'unknown', message: String(error), details: error, timestamp };
  }

  const message = error.message || '';
  const code = error.code || error.errorCode;

  if (
    message.includes('config') ||
    message.includes('issuer') ||
    message.includes('clientId') ||
    code === 'CONFIGURATION_ERROR'
  ) {
    return {
      type: 'configuration',
      message: message || 'Configuration error occurred',
      code,
      details: error,
      timestamp,
    };
  }
  if (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('timeout') ||
    code === 'NETWORK_ERROR'
  ) {
    return {
      type: 'network',
      message: message || 'Network error occurred',
      code,
      details: error,
      timestamp,
    };
  }
  if (
    message.includes('unauthorized') ||
    message.includes('forbidden') ||
    message.includes('access_denied') ||
    code === 'access_denied' ||
    code === 'AUTHORIZATION_ERROR'
  ) {
    return {
      type: 'authorization',
      message: message || 'Authorization error occurred',
      code,
      details: error,
      timestamp,
    };
  }
  if (
    message.includes('token') ||
    message.includes('expired') ||
    message.includes('invalid') ||
    code === 'TOKEN_ERROR'
  ) {
    return {
      type: 'token',
      message: message || 'Token error occurred',
      code,
      details: error,
      timestamp,
    };
  }
  return {
    type: 'unknown',
    message: message || 'An unknown error occurred',
    code,
    details: error,
    timestamp,
  };
}

export class AuthClient {
  private emitter = new Emitter();
  private state: IAuthState = { status: 'idle', user: null };
  private cachedSnapshot: IAuthState = { status: 'idle', user: null };
  private settings: IAuthClientConfig;
  private sessionCheckInterval?: ReturnType<typeof setInterval>;

  constructor(cfg: IAuthClientConfig) {
    this.settings = cfg;
  }

  on = this.emitter.on.bind(this.emitter);

  get snapshot(): IAuthState {
    return this.cachedSnapshot;
  }

  private mapSessionToState(data: IAuthSessionResponse): Pick<IAuthState, 'status' | 'user'> {
    if (!data.authenticated) {
      return { status: 'unauthenticated', user: null };
    }
    return {
      status: 'authenticated',
      user: {
        email: data.email,
        name: data.name,
        username: data.username,
        roles: data.roles,
      },
    };
  }

  private applySession(data: IAuthSessionResponse) {
    const { status, user } = this.mapSessionToState(data);
    this.setState({ status, user });
  }

  private setState(patch: Partial<IAuthState>) {
    const previousStatus = this.state.status;
    this.state = { ...this.state, ...patch };
    this.cachedSnapshot = { ...this.state };
    const newStatus = this.state.status;

    if (previousStatus !== newStatus) {
      if (newStatus === 'authenticated') {
        this.startSessionMonitoring();
      } else if (newStatus === 'unauthenticated' || newStatus === 'idle') {
        this.stopSessionMonitoring();
      }
    }

    this.emitter.emit('auth:change', { source: 'auth-client', ...this.snapshot });
  }

  /**
   * Initiates a standards-compliant OAuth 2.0 Authorization Code + PKCE login.
   *
   * 1. Calls POST /panel/auth-init with the user's email.
   * 2. Stores the returned `state` in sessionStorage for CSRF verification on callback.
   * 3. Builds the AS /authorize URL from the backend-provided params and redirects the browser.
   *
   * This works for any IDP (Okta, Google Workspace, etc.) — the backend resolves
   * the correct IDP and AS endpoints from the email domain.
   *
   * @param email The user's email address.
   * @param redirectTo Optional post-login destination within the app.
   */
  async signIn(email: string, redirectTo?: string) {
    this.setState({ status: 'loading' });
    try {
      const authInit = await AuthService.authInit(email);

      // Store CSRF state for verification on callback
      sessionStorage.setItem(AUTH_STATE_KEY, authInit.state);

      // Optionally stash the post-login destination
      if (redirectTo) {
        sessionStorage.setItem('auth_redirect_to', redirectTo);
      }

      const url = new URL(authInit.authorization_endpoint);
      url.searchParams.set('client_id', authInit.client_id);
      url.searchParams.set('redirect_uri', authInit.redirect_uri);
      url.searchParams.set('response_type', authInit.response_type);
      url.searchParams.set('scope', authInit.scope);
      url.searchParams.set('state', authInit.state);
      url.searchParams.set('code_challenge', authInit.code_challenge);
      url.searchParams.set('code_challenge_method', authInit.code_challenge_method);
      if (authInit.nonce) {
        url.searchParams.set('nonce', authInit.nonce);
      }

      window.location.assign(url.toString());
    } catch (error) {
      this.setState({ status: 'unauthenticated' });
      throw new AuthInitializationError('errors.auth.init.unknown', error, 'configuration');
    }
  }

  /**
   * Handles the OAuth callback after the AS redirects back.
   * Verifies state, exchanges code for session, and returns the post-login destination.
   *
   * @param code Authorization code from the AS redirect.
   * @param state State parameter from the AS redirect — must match what was stored.
   */
  async handleRedirect(code: string, state: string): Promise<string> {
    this.setState({ status: 'loading' });
    try {
      const storedState = sessionStorage.getItem(AUTH_STATE_KEY);
      if (!storedState || storedState !== state) {
        // Tagged with an explicit code rather than left to `categorizeError`'s message keywords,
        // which do not match this wording and so classified a possible CSRF attempt as "unknown".
        const stateMismatch: Error & { code?: string } = new Error(
          'State mismatch — possible CSRF attack'
        );
        stateMismatch.code = 'AUTHORIZATION_ERROR';
        throw stateMismatch;
      }

      const authResponse = await AuthService.login(code, state);

      sessionStorage.removeItem(AUTH_STATE_KEY);

      this.applySession(authResponse);

      const redirectTo = sessionStorage.getItem('auth_redirect_to');
      sessionStorage.removeItem('auth_redirect_to');

      const destination = redirectTo || ROUTE_PATHS.dashboard;
      return destination.startsWith('/ui') ? destination.substring(3) : destination;
    } catch (e) {
      sessionStorage.removeItem(AUTH_STATE_KEY);
      const error = categorizeError(e);
      this.setState({ status: 'unauthenticated', error });
      this.emitter.emit('auth:error', error as unknown as Record<string, unknown>);
      throw error;
    }
  }

  /** Creates AuthClient for a user who already has a valid session (skip auth-init). */
  static async createWithoutInit(
    defaultConfig: Partial<IAuthClientConfig>,
    sessionData: IAuthSessionResponse
  ): Promise<AuthClient> {
    const authClient = new AuthClient({
      redirectUri: defaultConfig.redirectUri ?? AUTH_CLIENT_CONFIG.redirectUri,
      postLogoutRedirectUri:
        defaultConfig.postLogoutRedirectUri ?? AUTH_CLIENT_CONFIG.postLogoutRedirectUri,
      scopes: defaultConfig.scopes ?? AUTH_CLIENT_CONFIG.scopes,
    });
    authClient.applySession(sessionData);
    return authClient;
  }

  /** Creates an AuthClient (no IDP config needed — resolved at sign-in time from email). */
  static async create(defaultConfig: Partial<IAuthClientConfig>): Promise<AuthClient> {
    return new AuthClient({
      redirectUri: defaultConfig.redirectUri ?? AUTH_CLIENT_CONFIG.redirectUri,
      postLogoutRedirectUri:
        defaultConfig.postLogoutRedirectUri ?? AUTH_CLIENT_CONFIG.postLogoutRedirectUri,
      scopes: defaultConfig.scopes ?? AUTH_CLIENT_CONFIG.scopes,
    });
  }

  async hydrateFromSession() {
    try {
      const sessionData = await AuthService.getCurrentSession();
      this.applySession(sessionData);
    } catch (error) {
      if (isApiError(error)) {
        const apiError = error as ApiError;
        if (apiError.status === 401 || apiError.status === 403) {
          this.setState({ status: 'unauthenticated', user: null });
          return;
        }
        if (apiError.status === 0 || apiError.code === 'NETWORK_ERROR') {
          return;
        }
      }
    }
  }

  startSessionMonitoring(intervalMs = 5 * 60 * 1000) {
    this.stopSessionMonitoring();
    if (this.state.status !== 'authenticated') return;
    this.sessionCheckInterval = setInterval(async () => {
      if (this.state.status === 'authenticated') {
        try {
          await this.hydrateFromSession();
        } catch {
          this.clearAuthState();
        }
      }
    }, intervalMs);
  }

  stopSessionMonitoring() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = undefined;
    }
  }

  clearAuthState() {
    this.stopSessionMonitoring();
    this.setState({ status: 'unauthenticated', user: null });
  }

  async signOut(postLogoutRedirectUri?: string) {
    this.stopSessionMonitoring();
    try {
      await AuthService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      this.setState({ status: 'unauthenticated', user: null });
      const redirectUri = postLogoutRedirectUri ?? this.settings.postLogoutRedirectUri;
      if (redirectUri) {
        window.location.replace(redirectUri);
      }
    }
  }

  hasRole(role: string): boolean {
    return !!this.state.user?.roles?.includes(role);
  }

  hasAnyRole(roles: string[]): boolean {
    const current = new Set(this.state.user?.roles ?? []);
    return roles.some((r) => current.has(r));
  }

  async init() {
    await this.hydrateFromSession();
    if (this.state.status === 'idle') {
      this.setState({ status: 'unauthenticated' });
    }
  }

  destroy() {
    this.stopSessionMonitoring();
  }
}

export const AUTH_CLIENT_CONFIG = {
  redirectUri: `${ROUTE_PATHS.loginCallback}`,
  postLogoutRedirectUri: `${ROUTE_PATHS.root}`,
  scopes: ['openid', 'profile', 'email'],
};

export { AUTH_STATE_KEY };
